/**
 * Live location tracking for the manager Inspections Dashboard.
 *
 * While a technician has the inspection workflow open, this continuously watches
 * the device position (navigator.geolocation.watchPosition) and persists a point
 * to event_logs as `inspection_location_heartbeat` whenever the tech has moved a
 * meaningful distance OR a keep-alive interval has elapsed. The dashboard reads
 * those rows to plot the tech's current position and draw the breadcrumb trail.
 *
 * Why watchPosition + a distance/time filter rather than polling every few
 * seconds: watchPosition keeps a single warm GPS fix (far better battery than
 * repeated cold getCurrentPosition calls), and the filter gives a dense, smooth
 * trail while moving without spamming identical points (and rows) while the tech
 * stands still. Capture is continuous; only meaningful points are stored.
 *
 * Permission: this tracker is strictly PASSIVE — it never raises its own
 * permission prompt. It only starts the watch once permission is already granted
 * (via the normal Set-GPS flow or the in-app Allow button). When not granted it
 * reports status via onResult so the workflow can show an explained, user-tap
 * prompt; it re-checks periodically to pick up a later grant.
 */
import { useEffect, useRef } from 'react';
import { logEvent } from './telemetry';
import { apiClient } from './apiClient';
import {
  clearLocationGranted,
  isLocationGranted,
  markLocationGranted,
  setLastKnownFix,
} from './locationState';

// Defaults for the trail granularity; overridable by admins via App Settings
// (tracking_min_distance_m / tracking_min_interval_sec).
const DEFAULT_MIN_DISTANCE_M = 8; // store a point once moved at least this far...
const DEFAULT_MIN_INTERVAL_MS = 12_000; // ...or at least this often (keep-alive while stationary)
const PERMISSION_RECHECK_MS = 10_000; // re-check for a grant while passive
const FAILURE_LOG_THROTTLE_MS = 5 * 60_000; // don't spam identical failures
const DEFAULT_GEOFENCE_RADIUS_M = 500; // beyond this from the property center = "left the site"
const GEOFENCE_EXIT_DEBOUNCE_MS = 20_000; // must stay confidently-outside this long before we stop
const DEFAULT_MAX_ACCURACY_M = 35; // don't record a fix less accurate than this (noise)

export interface TrackingConfig {
  minDistanceM: number;
  minIntervalMs: number;
  /** Stop tracking once the tech is confidently this far from the property center. */
  geofenceRadiusM: number;
  /** Don't record a fix whose reported accuracy is worse than this (metres). */
  maxAccuracyM: number;
}

function clampNum(raw: number, fallback: number, min: number, max: number): number {
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(Math.max(raw, min), max);
}

let cachedTrackingConfig: Promise<TrackingConfig> | null = null;

/**
 * Admin-configurable trail granularity, read once and cached. Missing/invalid
 * values fall back to the defaults. Distance 1–500 m, interval 2–600 s.
 */
export function loadTrackingConfig(): Promise<TrackingConfig> {
  if (cachedTrackingConfig) return cachedTrackingConfig;
  cachedTrackingConfig = (async () => {
    try {
      const [dist, intvl, geo, acc] = await Promise.all([
        apiClient.get<{ value: string | null }>('/api/app-settings/public/tracking_min_distance_m'),
        apiClient.get<{ value: string | null }>('/api/app-settings/public/tracking_min_interval_sec'),
        apiClient.get<{ value: string | null }>('/api/app-settings/public/tracking_geofence_radius_m'),
        apiClient.get<{ value: string | null }>('/api/app-settings/public/tracking_max_accuracy_m'),
      ]);
      return {
        minDistanceM: clampNum(parseFloat(dist.value ?? ''), DEFAULT_MIN_DISTANCE_M / 1, 1, 500),
        minIntervalMs: clampNum(parseFloat(intvl.value ?? ''), DEFAULT_MIN_INTERVAL_MS / 1000, 2, 600) * 1000,
        geofenceRadiusM: clampNum(parseFloat(geo.value ?? ''), DEFAULT_GEOFENCE_RADIUS_M, 50, 5000),
        maxAccuracyM: clampNum(parseFloat(acc.value ?? ''), DEFAULT_MAX_ACCURACY_M, 10, 200),
      };
    } catch {
      return {
        minDistanceM: DEFAULT_MIN_DISTANCE_M,
        minIntervalMs: DEFAULT_MIN_INTERVAL_MS,
        geofenceRadiusM: DEFAULT_GEOFENCE_RADIUS_M,
        maxAccuracyM: DEFAULT_MAX_ACCURACY_M,
      };
    }
  })();
  return cachedTrackingConfig;
}

export interface HeartbeatContext {
  reportId?: string | null;
  jobSiteId: string;
  jobSiteName: string;
  /**
   * The property's defined center (job_sites.gps_location). When present, the
   * tracker stops once the tech is confidently beyond the geofence radius from
   * here (e.g. driving back to the shop). When absent, geofencing is skipped and
   * tracking behaves as before.
   */
  anchorLat?: number | null;
  anchorLng?: number | null;
}

export interface HeartbeatResult {
  ok: boolean;
  errorName?: string;
  /** Set once the tech has left the property geofence and tracking has stopped. */
  left?: boolean;
  /** True while the tech is confidently beyond the property radius but was never
   *  on it this session — i.e. inspecting from the wrong place. */
  outOfBounds?: boolean;
  /** Metres from the configured property center (when known), for the warning. */
  distanceM?: number;
}

// The session grant flag, the last known fix, and the pure reads over them live
// in ./locationState — a leaf module with no imports. They are deliberately NOT
// re-exported from here: this file pulls React -> apiClient -> msalConfig, which
// touches `window` at module scope, so anything importing those names through
// this module becomes unloadable outside a browser. Import them from
// ./locationState directly.


function geoErrorName(code: number): string {
  switch (code) {
    case 1:
      return 'permission_denied';
    case 2:
      return 'position_unavailable';
    case 3:
      return 'timeout';
    default:
      return `unknown_${code}`;
  }
}

let lastFailureName = '';
let lastFailureLogAt = 0;

function logHeartbeatFailure(name: string, code: number | null, ctx: HeartbeatContext): void {
  const now = Date.now();
  if (name !== lastFailureName || now - lastFailureLogAt > FAILURE_LOG_THROTTLE_MS) {
    lastFailureName = name;
    lastFailureLogAt = now;
    void logEvent('inspection_location_heartbeat_failed', `Location heartbeat failed (${name})`, {
      severity: 'warning',
      metadata: {
        error: name,
        error_code: code,
        report_id: ctx.reportId ?? null,
        job_site_id: ctx.jobSiteId,
        job_site_name: ctx.jobSiteName,
      },
    });
  }
}

/** Great-circle distance in metres between two lat/lng points. */
function distanceM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const la1 = (aLat * Math.PI) / 180;
  const la2 = (bLat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Best-effort current geolocation permission state. Safari historically does
 * not support permissions.query for geolocation, so 'unknown' is common and
 * should be treated as "we may need to ask".
 */
export async function checkGeolocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      return status.state as 'granted' | 'denied' | 'prompt';
    }
  } catch {
    /* Safari throws for unsupported permission names — fall through */
  }
  return 'unknown';
}

export type PermissionRequestOutcome = 'granted' | 'denied' | 'error';

/**
 * Trigger the browser's location permission prompt. MUST be called from a user
 * gesture (e.g. a button tap) so iOS Safari actually shows the prompt. Resolves
 * 'granted' on success, 'denied' if the user/OS blocked location (error code 1),
 * or 'error' for any other failure (timeout, unavailable, no geolocation API).
 */
export function requestLocationPermission(): Promise<PermissionRequestOutcome> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve('error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => {
        markLocationGranted();
        resolve('granted');
      },
      (err) => resolve(err.code === 1 ? 'denied' : 'error'),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  });
}

/**
 * Continuously track and persist the technician's location for the lifetime of
 * the calling component. `onResult` fires on every position fix (ok) and on
 * failures (with the error kind) so the caller can prompt the tech to enable
 * location at any point during the inspection.
 */
export function useInspectionLocationHeartbeat(
  ctx: HeartbeatContext,
  onResult?: (result: HeartbeatResult) => void,
): void {
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const configRef = useRef<TrackingConfig>({
    minDistanceM: DEFAULT_MIN_DISTANCE_M,
    minIntervalMs: DEFAULT_MIN_INTERVAL_MS,
    geofenceRadiusM: DEFAULT_GEOFENCE_RADIUS_M,
    maxAccuracyM: DEFAULT_MAX_ACCURACY_M,
  });

  // Load admin-configured granularity (falls back to defaults until it resolves).
  useEffect(() => {
    let cancelled = false;
    void loadTrackingConfig().then((cfg) => {
      if (!cancelled) configRef.current = cfg;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      onResultRef.current?.({ ok: false, errorName: 'unavailable' });
      return;
    }

    let cancelled = false;
    let watchId = -1;
    let permTimer: ReturnType<typeof setInterval> | null = null;
    let last: { lat: number; lng: number; t: number } | null = null;
    // Geofence state: when did we first read confidently-outside, and have we
    // committed to "left the property" (which stops the watch for good).
    let outsideSince: number | null = null;
    let leftProperty = false;
    // Was the tech ever confidently INSIDE the property this session? Separates
    // "left after arriving" (stop tracking) from "never arrived / wrong property"
    // (warn but keep tracking so the manager still sees where they are).
    let wasInside = false;
    let outOfBoundsLoggedAt = 0;
    const OOB_LOG_THROTTLE_MS = 5 * 60_000;

    const persist = (lat: number, lng: number, acc: number | null) => {
      const c = ctxRef.current;
      void logEvent(
        'inspection_location_heartbeat',
        `Location update during inspection${c.jobSiteName ? ` at ${c.jobSiteName}` : ''}`,
        {
          metadata: {
            latitude: lat,
            longitude: lng,
            accuracy_m: acc,
            report_id: c.reportId ?? null,
            job_site_id: c.jobSiteId,
            job_site_name: c.jobSiteName,
          },
        },
      );
    };

    /** Stop the watch + the passive re-check loop. Used on geofence exit. */
    const stopTracking = () => {
      if (watchId !== -1) {
        navigator.geolocation.clearWatch(watchId);
        watchId = -1;
      }
      if (permTimer) {
        clearInterval(permTimer);
        permTimer = null;
      }
    };

    const onPosition = (pos: GeolocationPosition) => {
      if (cancelled || leftProperty) return;
      markLocationGranted();
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const acc = pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null;
      // Cache before any throttling/geofence logic below can discard this fix —
      // photo capture wants the freshest position we have seen, whether or not
      // it was worth persisting as a heartbeat.
      setLastKnownFix({ lat, lng, accuracyM: acc, at: Date.now() });
      const now = Date.now();
      const c = ctxRef.current;
      const { minDistanceM, minIntervalMs, geofenceRadiusM, maxAccuracyM } = configRef.current;

      // Geofence logic (only when the property center is known). Accuracy-gated:
      //   confidently inside  = dist + accuracy < radius  → on the property
      //   confidently outside = dist - accuracy > radius  → genuinely far
      //   (in between = too fuzzy to judge — do nothing, just keep tracking)
      if (c.anchorLat != null && c.anchorLng != null) {
        const dist = distanceM(c.anchorLat, c.anchorLng, lat, lng);
        const acc0 = acc ?? 0;
        if (dist + acc0 < geofenceRadiusM) {
          // On the property. Note arrival; clear any exit/out-of-bounds state.
          wasInside = true;
          outsideSince = null;
          onResultRef.current?.({ ok: true, outOfBounds: false });
        } else if (dist - acc0 > geofenceRadiusM) {
          if (wasInside) {
            // Arrived earlier, now confidently outside → left the site. Stop
            // recording the drive away; after a debounce, stop tracking for good.
            if (outsideSince == null) outsideSince = now;
            if (now - outsideSince >= GEOFENCE_EXIT_DEBOUNCE_MS) {
              leftProperty = true;
              void logEvent(
                'inspection_location_left_property',
                `Technician left ${c.jobSiteName || 'the property'} — location tracking stopped`,
                {
                  metadata: {
                    latitude: lat, longitude: lng, accuracy_m: acc,
                    distance_m: Math.round(dist), radius_m: geofenceRadiusM,
                    report_id: c.reportId ?? null,
                    job_site_id: c.jobSiteId, job_site_name: c.jobSiteName,
                  },
                },
              );
              stopTracking();
              onResultRef.current?.({ ok: true, left: true });
            } else {
              onResultRef.current?.({ ok: true }); // leaving, not yet committed
            }
            return; // don't record the off-property position (the drive away)
          }
          // Never arrived: the tech is doing the inspection somewhere other than
          // the configured property (wrong property selected, or a bad property
          // pin). Warn them, log it for reporting, but KEEP tracking so the
          // manager can see where they actually are.
          if (now - outOfBoundsLoggedAt >= OOB_LOG_THROTTLE_MS) {
            outOfBoundsLoggedAt = now;
            void logEvent(
              'inspection_out_of_bounds',
              `Inspection started off the configured property (${Math.round(dist)} m from ${c.jobSiteName || 'site'})`,
              {
                severity: 'warning',
                metadata: {
                  latitude: lat, longitude: lng, accuracy_m: acc,
                  distance_m: Math.round(dist), radius_m: geofenceRadiusM,
                  report_id: c.reportId ?? null,
                  job_site_id: c.jobSiteId, job_site_name: c.jobSiteName,
                },
              },
            );
          }
          onResultRef.current?.({ ok: true, outOfBounds: true, distanceM: Math.round(dist) });
        } else {
          // Accuracy straddles the boundary — inconclusive. Keep tracking.
          onResultRef.current?.({ ok: true });
        }
      } else {
        onResultRef.current?.({ ok: true });
      }

      // Drop fixes too inaccurate to trust (multipath spikes / coarse network
      // fixes) — they're the noise that makes the dashboard trail zigzag. Gate
      // the persist ONLY; the geofence logic above must still run on every fix.
      // Don't advance `last` on a dropped fix, so the distance/time filter keeps
      // measuring from the last GOOD point. Null/unknown accuracy is kept.
      const accTooPoor = acc != null && acc > maxAccuracyM;
      const movedFar = !last || distanceM(last.lat, last.lng, lat, lng) >= minDistanceM;
      const dueByTime = !last || now - last.t >= minIntervalMs;
      if (!accTooPoor && (movedFar || dueByTime)) {
        persist(lat, lng, acc);
        last = { lat, lng, t: now };
      }
    };

    const startWatch = () => {
      if (watchId !== -1 || leftProperty) return;
      watchId = navigator.geolocation.watchPosition(onPosition, onError, {
        enableHighAccuracy: true,
        maximumAge: 3000,
        timeout: 30_000,
      });
      if (permTimer) {
        clearInterval(permTimer);
        permTimer = null;
      }
    };

    const onError = (err: GeolocationPositionError) => {
      if (cancelled) return;
      const name = geoErrorName(err.code);
      if (name === 'permission_denied') {
        // Permission lost/blocked — stop the watch and go back to passive
        // re-checking so we never sit in a prompting/erroring loop.
        clearLocationGranted();
        if (watchId !== -1) {
          navigator.geolocation.clearWatch(watchId);
          watchId = -1;
        }
        if (!permTimer) permTimer = setInterval(() => void evaluate(), PERMISSION_RECHECK_MS);
      }
      logHeartbeatFailure(name, err.code, ctxRef.current);
      onResultRef.current?.({ ok: false, errorName: name });
    };

    const evaluate = async () => {
      if (cancelled || watchId !== -1 || leftProperty) return;
      const state = await checkGeolocationPermission();
      if (cancelled || watchId !== -1 || leftProperty) return;
      if (state === 'granted') markLocationGranted();
      if (state === 'denied') {
        onResultRef.current?.({ ok: false, errorName: 'permission_denied' });
        return;
      }
      if (state === 'granted' || isLocationGranted()) {
        startWatch();
        return;
      }
      // 'prompt' / 'unknown' (iOS) and never granted — do NOT prompt; report so
      // the workflow can show an explained, user-initiated prompt.
      onResultRef.current?.({ ok: false, errorName: 'not_granted' });
    };

    void evaluate();
    permTimer = setInterval(() => void evaluate(), PERMISSION_RECHECK_MS);

    return () => {
      cancelled = true;
      if (watchId !== -1) navigator.geolocation.clearWatch(watchId);
      if (permTimer) clearInterval(permTimer);
    };
  }, []);
}
